import { useCallback, useState } from "react";
import Api, { MyQuotesListResponse } from "../../Api";
import EntityList from "../../parts/EntityList";
import EntitySearch from "../../parts/EntitySearch";
import { Badge } from "./List";
import { getAmounts, intToDec } from "../../utils";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { Link } from "react-router-dom";

const ListMine = () => {
  const [data, setData] = useState<MyQuotesListResponse[] | null>(null);

  const search = useCallback(
    (searchTerm: string, page: string) =>
      Api.myQuotesList("me", searchTerm, page),
    []
  );
  return (
    <>
      <div className="px-4">
        <EntitySearch searchFunction={search} setEntities={setData} />
      </div>
      <EntityList entities={data} Card={QuoteCard} />
    </>
  );
};

export default ListMine;

const QuoteCard = ({
  entity: { quote, clientName },
  expanded,
  setExpanded,
}: {
  entity: MyQuotesListResponse;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}) => {
  const amounts = getAmounts(quote);
  return (
    <div className={classNames("border rounded-lg shadow-sm")} key={quote.uuid}>
      <div
        className={classNames("p-4 flex flex-col gap-2", {
          "hover:bg-slate-50 cursor-pointer": !expanded,
        })}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex gap-1">
          <Badge className="font-mono cursor-default">{quote.number}</Badge>

          <Badge>
            <FontAwesomeIcon icon="calendar" className="mr-2 text-gray-400" />
            {moment(quote.creationDate).format("DD/MM/YY")}
          </Badge>

          {!quote.isApproved && (
            <Badge className="bg-yellow-100 text-yellow-600">
              <FontAwesomeIcon icon="clock" className="mr-2" />
              Pendente
            </Badge>
          )}
        </div>

        <div className="overflow-scroll whitespace-nowrap no-scrollbar font-semibold">
          {clientName}
        </div>

        <div className="flex gap-1">
          {amounts.finalSale > 0 && (
            <div className="bg-cyan-100 text-cyan-800 rounded text-xs px-1.5 py-0.5 tracking-tighter">
              <span className="font-light">Venda</span>{" "}
              <span className="font-bold">{intToDec(amounts.finalSale)}</span>
            </div>
          )}

          {amounts.finalRent > 0 && (
            <div className="bg-lime-100 text-lime-800 rounded text-xs px-1.5 py-0.5 tracking-tighter">
              <span className="font-light">Mensal</span>{" "}
              <span className="font-bold">{intToDec(amounts.finalRent)}</span>
            </div>
          )}

          {amounts.fee > 0 && (
            <div className="bg-violet-100 text-violet-900 rounded text-xs px-1.5 py-0.5 tracking-tighter">
              <span className="font-light">Taxa</span>{" "}
              <span className="font-bold">{intToDec(amounts.fee)}</span>
            </div>
          )}
        </div>
      </div>

      {quote.isAuthorized && (
        <div className="text-gray-400 truncate text-xs font-light border-t px-4 py-1">
          <span className="font-semibold text-gray-500">
            <FontAwesomeIcon
              icon="check-circle"
              className="text-gray-400 mr-3"
            />
            Autorizado
          </span>
          {quote.authorizationDate && (
            <>
              {" "}
              em {moment(quote.authorizationDate).format("DD/MM/YY")} às{" "}
              {moment(quote.authorizationDate).format("HH:mm")}
            </>
          )}
        </div>
      )}

      {expanded && (
        <div className="flex border-t text-xs">
          <div className="flex overflow-scroll no-scrollbar">
            {!quote.isApproved && (
              <Link
                className="p-3 border-r font-bold hover:bg-slate-50 rounded-bl-lg"
                to={`/meus-orcamentos/visualizar/${quote.uuid}`}
                target="_blank"
              >
                Abrir
              </Link>
            )}

            {quote.isApproved && (
              <Link
                className="p-3 border-r font-bold hover:bg-slate-50 rounded-bl-lg"
                to={`/externo/proposta/${quote.externalHash}`}
                target="_blank"
              >
                Abrir
              </Link>
            )}

            {!quote.isApproved && (
              <Link
                className="p-3 border-r font-bold hover:bg-slate-50"
                to={`/meus-orcamentos/alterar/${quote.uuid}`}
              >
                Alterar
              </Link>
            )}

            <Link
              className="p-3 font-bold hover:bg-slate-50"
              to={`/meus-orcamentos/duplicar/${quote.uuid}`}
            >
              Duplicar
            </Link>
          </div>

          <button
            className="rounded-br-lg p-3 flex-1 text-right hover:bg-slate-50 border-l"
            onClick={() => setExpanded(false)}
          >
            <FontAwesomeIcon icon="chevron-up" />
          </button>
        </div>
      )}
    </div>
  );
};
